import React, { useState, useEffect } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { MailIcon } from '../components/Icons';
import { API_ENDPOINTS } from '../config/api';

export const EmailVerificationPage = () => {
  const [email, setEmail] = useState('');
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Get email from localStorage if available
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const user = JSON.parse(storedUser);
      setEmail(user.email || '');
    }

    // Check if there's a token in the URL
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    if (token) {
      verifyEmail(token);
    }
  }, []);

  useEffect(() => {
    if (resendTimer > 0 && !canResend) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    } else if (resendTimer === 0) {
      setCanResend(true);
    }
  }, [resendTimer, canResend]);

  const verifyEmail = async (token: string) => {
    setVerifying(true);
    setError('');

    try {
      const response = await fetch(API_ENDPOINTS.auth.verifyEmail, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Verification failed');
        setVerifying(false);
        return;
      }

      // Verification successful
      setVerified(true);
      setVerifying(false);

      // Get user role and redirect to appropriate dashboard after 2 seconds
      setTimeout(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const user = JSON.parse(storedUser);
          const role = user.role;

          if (role === 'WRITER') {
            window.location.href = '/writer/dashboard';
          } else if (role === 'READER') {
            window.location.href = '/reader/dashboard';
          } else if (role === 'AGENT') {
            window.location.href = '/agent/dashboard';
          } else {
            window.location.href = '/';
          }
        } else {
          window.location.href = '/';
        }
      }, 2000);
    } catch (err) {
      console.error('Verification error:', err);
      setError('Network error. Please try again.');
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    if (!canResend || !email) return;

    try {
      const response = await fetch(API_ENDPOINTS.auth.resendVerification, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to resend email');
        return;
      }

      setShowSuccess(true);
      setCanResend(false);
      setResendTimer(60);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      console.error('Resend error:', err);
      setError('Network error. Please try again.');
    }
  };

  const handleChangeEmail = () => {
    window.location.href = '/register';
  };

  // Show verifying state
  if (verifying) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: theme.colors.background,
        fontFamily: theme.fonts.primary,
        display: 'flex',
        flexDirection: 'column',
      }}>
        <Header showAuth={false} />
        <main style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px',
        }}>
          <div style={{
            maxWidth: '600px',
            width: '100%',
            textAlign: 'center',
          }}>
            <h1 style={{
              fontSize: '40px',
              fontWeight: '400',
              marginBottom: '16px',
              color: theme.colors.textPrimary,
            }}>
              Verifying Your Email...
            </h1>
            <p style={{
              fontSize: '18px',
              color: theme.colors.textSecondary,
            }}>
              Please wait while we verify your email address.
            </p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Show success state
  if (verified) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: theme.colors.background,
        fontFamily: theme.fonts.primary,
        display: 'flex',
        flexDirection: 'column',
      }}>
        <Header showAuth={false} />
        <main style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px',
        }}>
          <div style={{
            maxWidth: '600px',
            width: '100%',
            textAlign: 'center',
          }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '80px',
              height: '80px',
              backgroundColor: theme.colors.primaryLight,
              borderRadius: '50%',
              color: theme.colors.primary,
              marginBottom: '32px',
              fontSize: '48px',
            }}>
              ✓
            </div>
            <h1 style={{
              fontSize: '40px',
              fontWeight: '400',
              marginBottom: '16px',
              color: theme.colors.textPrimary,
            }}>
              Email Verified!
            </h1>
            <p style={{
              fontSize: '18px',
              color: theme.colors.textSecondary,
              marginBottom: '24px',
            }}>
              Your email has been successfully verified. Redirecting you to your dashboard...
            </p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Show default "check your email" state
  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
      display: 'flex',
      flexDirection: 'column',
    }}>
      <Header showAuth={false} />

      <main style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px',
      }}>
        <div style={{
          maxWidth: '600px',
          width: '100%',
          textAlign: 'center',
        }}>
          {/* Icon */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '80px',
            height: '80px',
            backgroundColor: theme.colors.primaryLight,
            borderRadius: '50%',
            color: theme.colors.primary,
            marginBottom: '32px',
          }}>
            <MailIcon width={40} height={40} />
          </div>

          {/* Title */}
          <h1 style={{
            fontSize: '40px',
            fontWeight: '400',
            marginBottom: '16px',
            color: theme.colors.textPrimary,
          }}>
            Check Your Email
          </h1>

          {/* Description */}
          <p style={{
            fontSize: '18px',
            lineHeight: '1.6',
            color: theme.colors.textSecondary,
            marginBottom: '32px',
          }}>
            We've sent a verification link to
          </p>

          {/* Email Display */}
          {email && (
            <div style={{
              backgroundColor: theme.colors.white,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius.large,
              padding: '16px 24px',
              marginBottom: '32px',
              display: 'inline-block',
            }}>
              <span style={{
                fontSize: '16px',
                fontWeight: '500',
                color: theme.colors.textPrimary,
                fontFamily: theme.fonts.monospace,
              }}>
                {email}
              </span>
            </div>
          )}

          {/* Instructions */}
          <div style={{
            backgroundColor: theme.colors.white,
            border: `1px solid ${theme.colors.border}`,
            borderRadius: theme.borderRadius.xlarge,
            padding: '32px',
            marginBottom: '32px',
            textAlign: 'left',
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '500',
              marginBottom: '16px',
              color: theme.colors.textPrimary,
            }}>
              What's next?
            </h3>
            <ol style={{
              paddingLeft: '20px',
              margin: 0,
              color: theme.colors.textSecondary,
              fontSize: '16px',
              lineHeight: '1.8',
            }}>
              <li>Check your email inbox (and spam folder)</li>
              <li>Click the verification link in the email</li>
              <li>You'll be redirected to complete your profile</li>
            </ol>
          </div>

          {/* Error Message */}
          {error && (
            <div style={{
              backgroundColor: theme.colors.errorLight,
              border: `1px solid ${theme.colors.error}`,
              borderRadius: theme.borderRadius.large,
              padding: '16px',
              marginBottom: '24px',
              color: theme.colors.error,
              fontSize: '15px',
            }}>
              {error}
            </div>
          )}

          {/* Success Message */}
          {showSuccess && (
            <div style={{
              backgroundColor: theme.colors.primaryLight,
              border: `1px solid ${theme.colors.primary}`,
              borderRadius: theme.borderRadius.large,
              padding: '16px',
              marginBottom: '24px',
              color: theme.colors.primaryDark,
              fontSize: '15px',
            }}>
              Verification email sent successfully!
            </div>
          )}

          {/* Actions */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            alignItems: 'center',
          }}>
            <Button
              onClick={handleResend}
              disabled={!canResend || !email}
              variant="primary"
            >
              {canResend ? 'Resend Email' : `Resend in ${resendTimer}s`}
            </Button>

            <Button
              onClick={handleChangeEmail}
              variant="ghost"
            >
              Change Email Address
            </Button>
          </div>

          {/* Help Text */}
          <div style={{
            marginTop: '32px',
            padding: '24px',
            backgroundColor: theme.colors.backgroundAlt,
            borderRadius: theme.borderRadius.large,
            fontSize: '14px',
            color: theme.colors.textSecondary,
            lineHeight: '1.6',
          }}>
            <strong style={{ color: theme.colors.textPrimary }}>Didn't receive the email?</strong>
            <br />
            Check your spam folder or try adding noreply@manuscriptmatch.com to your contacts.
            If you still need help, contact our support team.
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};
